// Quelle: /opt/webgis/frontend/src/components/ui/Modal.tsx (Stand 2026-09-29)
// Geändert: aus /opt/lilasprojekte/frontend/src/components/ui/Modal.tsx eingepfropft — Fokus
// wandert beim Öffnen in das Blatt und beim Schließen zum Auslöser zurück, die Seite darunter
// rollt nicht mit, role="dialog" aria-modal="true" aria-labelledby; dazu eine Tab-Falle.
// Radius var(--r-modal). Escape über den Esc-Stapel (store/useStore `ebeneAnmelden`) statt
// eines eigenen window-keydown (SPEC 03 §8.4): nur die OBERSTE Ebene schließt.
import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ebeneAnmelden } from "../../store/useStore";
import { Icon } from "./Icon";
import { IconBtn, portalRoot } from "./primitives";

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  width?: number;
  title?: string;
  icon?: string;
  subtitle?: string;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /** Aktionen in der Kopfzeile links vom X (z. B. Modus-Umschalter) */
  headerExtra?: ReactNode;
  /** Während einer laufenden Arbeit (Export schreiben) schließen weder Esc noch Grund. */
  gesperrt?: boolean;
}

const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  children,
  width = 520,
  title,
  icon,
  subtitle,
  footer,
  closeOnBackdrop = true,
  headerExtra,
  gesperrt = false,
}: ModalProps) {
  const titelId = useId();
  const ebeneId = useId();
  const blattRef = useRef<HTMLDivElement>(null);
  const vorherRef = useRef<Element | null>(null);
  // Der Schließer über einen Ref: so meldet sich die Ebene nicht bei jedem neuen onClose-
  // Pfeil (jede Zeichnung des Aufrufers) ab und wieder an und verrutscht im Stapel.
  const schliessen = useRef<() => void>(() => undefined);
  schliessen.current = () => {
    if (!gesperrt && onClose) onClose();
  };

  useEffect(() => {
    if (!open) return;
    return ebeneAnmelden("modal:" + ebeneId, () => schliessen.current());
  }, [open, ebeneId]);

  useEffect(() => {
    if (!open) return;
    vorherRef.current = document.activeElement;
    // Das Blatt selbst bekommt den Fokus (tabIndex -1) und nicht das erste
    // Feld darin: ein Modal, das mit einem markierten Eingabefeld aufgeht,
    // verschluckt auf dem Telefon die erste Tastatureingabe.
    blattRef.current?.focus({ preventScroll: true });
    const alt = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = alt;
      const zurueck = vorherRef.current;
      if (zurueck instanceof HTMLElement) zurueck.focus({ preventScroll: true });
    };
  }, [open]);

  // Tab-Falle: der Fokus bleibt im Blatt (modaler Dialog, WAI-ARIA).
  const falle = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !blattRef.current) return;
    const ziele = Array.from(blattRef.current.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (ziele.length === 0) {
      e.preventDefault();
      return;
    }
    const erstes = ziele[0]!;
    const letztes = ziele[ziele.length - 1]!;
    if (e.shiftKey && (document.activeElement === erstes || document.activeElement === blattRef.current)) {
      e.preventDefault();
      letztes.focus();
    } else if (!e.shiftKey && document.activeElement === letztes) {
      e.preventDefault();
      erstes.focus();
    }
  };

  if (!open) return null;
  const telefon = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 640px)").matches;
  return createPortal(
    <div
      className="anim-fade modal-wrap"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titelId : undefined}
      aria-label={title ? undefined : "Dialog"}
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={() => closeOnBackdrop && schliessen.current()}
        style={{ position: "absolute", inset: 0, background: "var(--modal-scrim)", backdropFilter: "blur(4px)" }}
      />
      <div
        ref={blattRef}
        tabIndex={-1}
        onKeyDown={falle}
        className="glass-strong anim-pop scroll modal-card"
        // dvh statt vh: mobile Browserleisten schrumpfen den sichtbaren Bereich —
        // mit 90vh ragte das zentrierte Modal oben UND unten aus dem Schirm
        style={{
          position: "relative",
          width,
          maxWidth: "100%",
          maxHeight: "min(90vh, calc(100dvh - 32px))",
          overflowY: "auto",
          borderRadius: "var(--r-modal)",
          boxShadow: "var(--shadow-lg)",
          outline: "none",
          ...(telefon ? { background: "var(--panel-solid)", backdropFilter: "none" } : {}),
        }}
      >
        {title && (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "22px 24px 16px" }}>
            <div style={{ display: "flex", gap: 13, minWidth: 0 }}>
              {icon && (
                <div
                  className="mobile-hide"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: "var(--accent-soft)",
                    color: "var(--accent-text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                  }}
                >
                  <Icon name={icon} size={20} />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <h3 id={titelId} className="disp" style={{ fontSize: 19 }}>
                  {title}
                </h3>
                {subtitle && <p style={{ fontSize: 13.5, color: "var(--text-mute)", marginTop: 3 }}>{subtitle}</p>}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
              {headerExtra}
              {onClose && <IconBtn type="button" icon="x" size={18} box={34} onClick={() => schliessen.current()} title="Schließen" disabled={gesperrt} />}
            </div>
          </div>
        )}
        <div style={{ padding: title ? "0 24px 22px" : 24 }}>{children}</div>
        {footer && (
          <div className="modal-foot" style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid var(--glass-border)" }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalRoot(),
  );
}
